import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store/store'

interface DashboardState {
  // Refresh settings
  refreshInterval: number // milliseconds
  autoRefresh: boolean
  lastRefreshed: string | null

  // Time range settings
  timeRange: 'today' | '7days' | '30days' | 'custom'
  customDateRange: {
    start: string | null
    end: string | null
  }

  // View preferences
  viewMode: 'grid' | 'list' | 'compact'
  expandedSections: string[]
  collapsedMetrics: string[]

  // Filters
  selectedRegion: string | null
  selectedBranch: string | null
  selectedRole: string | null
  selectedMetricType: 'all' | 'users' | 'enrollments' | 'system' | 'security'

  // Chart preferences
  chartPreferences: {
    userActivity: {
      type: 'line' | 'area' | 'bar'
      metric: 'all' | 'activeUsers' | 'newRegistrations' | 'enrollments' | 'apiCalls'
    }
    enrollmentStats: {
      view: 'timeline' | 'distribution' | 'comparison'
      groupBy: 'day' | 'week' | 'month'
    }
    regionalView: {
      metric: 'enrollments' | 'users' | 'performance'
      sortBy: 'name' | 'value' | 'trend'
    }
  }

  // Notification preferences
  notifications: {
    showAlerts: boolean
    alertSeverity: 'all' | 'high' | 'critical'
    soundEnabled: boolean
  }

  // Layout preferences
  layout: {
    sidebarCollapsed: boolean
    fullscreenMode: boolean
    compactMode: boolean
    darkMode: boolean
  }

  // Bookmarks
  bookmarkedViews: Array<{
    id: string
    name: string
    config: Partial<DashboardState>
    createdAt: string
  }>

  // Cache
  cachedData: {
    timestamp: string | null
    expiresAt: string | null
  }
}

const initialState: DashboardState = {
  // Refresh settings
  refreshInterval: 30000, // 30 seconds
  autoRefresh: true,
  lastRefreshed: null,

  // Time range settings
  timeRange: 'today',
  customDateRange: {
    start: null,
    end: null,
  },

  // View preferences
  viewMode: 'grid',
  expandedSections: [],
  collapsedMetrics: [],

  // Filters
  selectedRegion: null,
  selectedBranch: null,
  selectedRole: null,
  selectedMetricType: 'all',

  // Chart preferences
  chartPreferences: {
    userActivity: {
      type: 'area',
      metric: 'all',
    },
    enrollmentStats: {
      view: 'timeline',
      groupBy: 'day',
    },
    regionalView: {
      metric: 'enrollments',
      sortBy: 'value',
    },
  },

  // Notification preferences
  notifications: {
    showAlerts: true,
    alertSeverity: 'high',
    soundEnabled: false,
  },

  // Layout preferences
  layout: {
    sidebarCollapsed: false,
    fullscreenMode: false,
    compactMode: false,
    darkMode: false,
  },

  // Bookmarks
  bookmarkedViews: [],

  // Cache
  cachedData: {
    timestamp: null,
    expiresAt: null,
  },
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Refresh settings
    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    updateLastRefreshed: (state) => {
      state.lastRefreshed = new Date().toISOString()
    },

    // Time range settings
    setTimeRange: (state, action: PayloadAction<DashboardState['timeRange']>) => {
      state.timeRange = action.payload
    },

    setCustomDateRange: (state, action: PayloadAction<{ start: string; end: string }>) => {
      state.customDateRange = action.payload
      state.timeRange = 'custom'
    },

    // View preferences
    setViewMode: (state, action: PayloadAction<DashboardState['viewMode']>) => {
      state.viewMode = action.payload
    },

    toggleSection: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload
      const index = state.expandedSections.indexOf(sectionId)

      if (index > -1) {
        state.expandedSections.splice(index, 1)
      } else {
        state.expandedSections.push(sectionId)
      }
    },

    toggleMetric: (state, action: PayloadAction<string>) => {
      const metricId = action.payload
      const index = state.collapsedMetrics.indexOf(metricId)

      if (index > -1) {
        state.collapsedMetrics.splice(index, 1)
      } else {
        state.collapsedMetrics.push(metricId)
      }
    },

    // Filters
    setSelectedRegion: (state, action: PayloadAction<string | null>) => {
      state.selectedRegion = action.payload
      state.selectedBranch = null // Reset branch when region changes
    },

    setSelectedBranch: (state, action: PayloadAction<string | null>) => {
      state.selectedBranch = action.payload
    },

    setSelectedRole: (state, action: PayloadAction<string | null>) => {
      state.selectedRole = action.payload
    },

    setSelectedMetricType: (state, action: PayloadAction<DashboardState['selectedMetricType']>) => {
      state.selectedMetricType = action.payload
    },

    // Chart preferences
    setUserActivityChartType: (state, action: PayloadAction<'line' | 'area' | 'bar'>) => {
      state.chartPreferences.userActivity.type = action.payload
    },

    setUserActivityMetric: (
      state,
      action: PayloadAction<DashboardState['chartPreferences']['userActivity']['metric']>
    ) => {
      state.chartPreferences.userActivity.metric = action.payload
    },

    setEnrollmentStatsView: (
      state,
      action: PayloadAction<'timeline' | 'distribution' | 'comparison'>
    ) => {
      state.chartPreferences.enrollmentStats.view = action.payload
    },

    setEnrollmentStatsGroupBy: (state, action: PayloadAction<'day' | 'week' | 'month'>) => {
      state.chartPreferences.enrollmentStats.groupBy = action.payload
    },

    setRegionalViewMetric: (
      state,
      action: PayloadAction<'enrollments' | 'users' | 'performance'>
    ) => {
      state.chartPreferences.regionalView.metric = action.payload
    },

    setRegionalViewSortBy: (state, action: PayloadAction<'name' | 'value' | 'trend'>) => {
      state.chartPreferences.regionalView.sortBy = action.payload
    },

    // Notification preferences
    setNotificationPreferences: (
      state,
      action: PayloadAction<Partial<DashboardState['notifications']>>
    ) => {
      state.notifications = { ...state.notifications, ...action.payload }
    },

    // Layout preferences
    toggleSidebar: (state) => {
      state.layout.sidebarCollapsed = !state.layout.sidebarCollapsed
    },

    setFullscreenMode: (state, action: PayloadAction<boolean>) => {
      state.layout.fullscreenMode = action.payload
    },

    setCompactMode: (state, action: PayloadAction<boolean>) => {
      state.layout.compactMode = action.payload
    },

    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.layout.darkMode = action.payload
    },

    // Bookmarks
    addBookmark: (
      state,
      action: PayloadAction<{ name: string; config: Partial<DashboardState> }>
    ) => {
      const bookmark = {
        id: `bookmark-${Date.now()}`,
        name: action.payload.name,
        config: action.payload.config,
        createdAt: new Date().toISOString(),
      }
      state.bookmarkedViews.push(bookmark)
    },

    removeBookmark: (state, action: PayloadAction<string>) => {
      state.bookmarkedViews = state.bookmarkedViews.filter((b) => b.id !== action.payload)
    },

    applyBookmark: (state, action: PayloadAction<string>) => {
      const bookmark = state.bookmarkedViews.find((b) => b.id === action.payload)
      if (bookmark) {
        // Apply bookmarked configuration
        Object.assign(state, bookmark.config)
      }
    },

    // Cache
    setCacheTimestamp: (state, action: PayloadAction<{ expiresIn?: number }>) => {
      state.cachedData.timestamp = new Date().toISOString()
      const expiresIn = action.payload.expiresIn || 300000 // 5 minutes default
      state.cachedData.expiresAt = new Date(Date.now() + expiresIn).toISOString()
    },

    clearCache: (state) => {
      state.cachedData = {
        timestamp: null,
        expiresAt: null,
      }
    },

    // Reset
    resetDashboardState: () => initialState,

    // Restore state
    restoreState: (state, action: PayloadAction<Partial<DashboardState>>) => {
      return { ...state, ...action.payload }
    },
  },
})

// Actions
export const {
  setRefreshInterval,
  setAutoRefresh,
  updateLastRefreshed,
  setTimeRange,
  setCustomDateRange,
  setViewMode,
  toggleSection,
  toggleMetric,
  setSelectedRegion,
  setSelectedBranch,
  setSelectedRole,
  setSelectedMetricType,
  setUserActivityChartType,
  setUserActivityMetric,
  setEnrollmentStatsView,
  setEnrollmentStatsGroupBy,
  setRegionalViewMetric,
  setRegionalViewSortBy,
  setNotificationPreferences,
  toggleSidebar,
  setFullscreenMode,
  setCompactMode,
  setDarkMode,
  addBookmark,
  removeBookmark,
  applyBookmark,
  setCacheTimestamp,
  clearCache,
  resetDashboardState,
  restoreState,
} = dashboardSlice.actions

// Selectors
export const selectRefreshInterval = (state: RootState) => state.dashboard.refreshInterval
export const selectAutoRefresh = (state: RootState) => state.dashboard.autoRefresh
export const selectTimeRange = (state: RootState) => state.dashboard.timeRange
export const selectCustomDateRange = (state: RootState) => state.dashboard.customDateRange
export const selectViewMode = (state: RootState) => state.dashboard.viewMode
export const selectExpandedSections = (state: RootState) => state.dashboard.expandedSections
export const selectSelectedRegion = (state: RootState) => state.dashboard.selectedRegion
export const selectSelectedBranch = (state: RootState) => state.dashboard.selectedBranch
export const selectChartPreferences = (state: RootState) => state.dashboard.chartPreferences
export const selectNotificationPreferences = (state: RootState) => state.dashboard.notifications
export const selectLayoutPreferences = (state: RootState) => state.dashboard.layout
export const selectBookmarkedViews = (state: RootState) => state.dashboard.bookmarkedViews
export const selectCacheStatus = (state: RootState) => state.dashboard.cachedData

// Computed selectors
export const selectIsCacheValid = (state: RootState) => {
  const { expiresAt } = state.dashboard.cachedData
  if (!expiresAt) return false
  return new Date(expiresAt) > new Date()
}

export const selectActiveFilters = (state: RootState) => {
  const filters: Record<string, any> = {}

  if (state.dashboard.selectedRegion) {
    filters['region'] = state.dashboard.selectedRegion
  }
  if (state.dashboard.selectedBranch) {
    filters['branch'] = state.dashboard.selectedBranch
  }
  if (state.dashboard.selectedRole) {
    filters['role'] = state.dashboard.selectedRole
  }

  return filters
}

// Reducer
export default dashboardSlice.reducer
